import { AnchorType, getProgressStatus } from '@gearbelt/global';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { match } from 'ts-pattern';

import CheckIcon from '@/components/icons/check';
import InformationIcon from '@/components/icons/information';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface UnitProgress {
  remaining: number;
  unitName: string;
  unitAbbreviation: string;
  progress: number;
}

export interface DateProgress {
  days: number;
  progress: number;
  calendarUnit?: 'd' | 'w' | 'm';
}

export interface CheckProgressBarProps {
  isClosed: boolean;
  isUnlinked: boolean;
  anchorType?: AnchorType;
  hasScheduleCycle?: boolean;
  dueProgress: number | null;
  dateRemaining: { days: number } | null;
  showTooltip?: boolean;
  allUnitProgress?: UnitProgress[] | null;
  allDateProgress?: DateProgress[] | null;
}

export function getProgressColors(status: 'overdue' | 'due' | 'ok') {
  return match(status)
    .with('overdue', () => ({
      primary: 'rgb(var(--default-red))',
      secondary: 'rgb(var(--secondary-red))',
    }))
    .with('due', () => ({
      primary: 'rgb(var(--default-yellow))',
      secondary: 'rgb(var(--secondary-yellow))',
    }))
    .with('ok', () => ({
      primary: 'rgb(var(--default-green))',
      secondary: 'rgb(var(--secondary-green))',
    }))
    .exhaustive();
}

export function ProgressBar({ dueProgress, dateRemaining }: { dueProgress: number; dateRemaining: { days: number } | null }) {
  const progressStatus = getProgressStatus(dueProgress, dateRemaining);
  const colors = getProgressColors(progressStatus);

  return (
    <div className="bg-grey-2 h-3 w-full overflow-hidden rounded-full">
      <div
        className="h-full rounded-full"
        style={{
          width: dueProgress < 0 ? 0 : `max(12px, ${Math.min(dueProgress, 100)}%)`,
          background: `repeating-linear-gradient(
            45deg,
            ${colors.primary},
            ${colors.primary} 8px,
            ${colors.secondary} 8px,
            ${colors.secondary} 16px
          )`,
        }}
      />
    </div>
  );
}

export function UnlinkedProgressBar() {
  return (
    <div className="mt-1 flex w-full items-center">
      <div className="bg-grey-2 h-3 flex-1 rounded-l-full" />
      <InformationIcon className="text-grey-5 mx-1 h-4 w-4 shrink-0" />
      <div className="bg-grey-2 h-3 flex-1 rounded-r-full" />
    </div>
  );
}

function ProgressTooltipContent({
  allUnitProgress,
  allDateProgress,
  dateRemaining,
}: {
  allUnitProgress?: UnitProgress[] | null;
  allDateProgress?: DateProgress[] | null;
  dateRemaining: { days: number } | null;
}) {
  return (
    <ul className="flex flex-col gap-1">
      {allUnitProgress?.map((unit, index) => (
        <li key={`unit-${index}`} className="flex items-center gap-2 text-xs">
          <span>•</span>
          <span>
            {unit.remaining.toLocaleString()} {unit.unitAbbreviation} remaining <span className="font-semibold">({unit.progress.toFixed(0)}%)</span>
          </span>
        </li>
      ))}
      {allDateProgress && allDateProgress.length > 0
        ? allDateProgress.map((d, index) => {
            const unitLabel = match(d.calendarUnit)
              .with('d', () => 'Days')
              .with('w', () => 'Weeks')
              .with('m', () => 'Months')
              .otherwise(() => 'Days');
            return (
              <li key={`date-${index}`} className="flex items-center gap-2 text-xs">
                <span>•</span>
                <span>
                  {`${unitLabel}: ${d.days} days remaining`} <span className="font-semibold">({d.progress.toFixed(0)}%)</span>
                </span>
              </li>
            );
          })
        : dateRemaining && (
            <li className="flex items-center gap-2 text-xs">
              <span>•</span>
              <span>{`${dateRemaining.days} days remaining`}</span>
            </li>
          )}
    </ul>
  );
}

function WrapTooltip({ children, content }: { children: React.ReactNode; content: React.ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div>{children}</div>
        </TooltipTrigger>
        <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
          <TooltipArrow className="fill-default-text" />
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function CheckProgressBar({
  isClosed,
  isUnlinked,
  anchorType,
  hasScheduleCycle = false,
  dueProgress,
  dateRemaining,
  showTooltip = false,
  allUnitProgress,
  allDateProgress,
}: CheckProgressBarProps) {
  if (isClosed) {
    return <CheckIcon className="text-default-green h-4 w-4" />;
  }

  if (isUnlinked) {
    if (showTooltip) {
      const unlinkedTooltip =
        anchorType === AnchorType.Crawling && hasScheduleCycle
          ? 'Due progress appears after the first check is closed'
          : "Pick a schedule check to define this asset's starting position";
      return (
        <WrapTooltip content={unlinkedTooltip}>
          <UnlinkedProgressBar />
        </WrapTooltip>
      );
    }
    return <UnlinkedProgressBar />;
  }

  if (dueProgress == null) return null;

  const progressBar = <ProgressBar dueProgress={dueProgress} dateRemaining={dateRemaining} />;

  if (showTooltip) {
    const hasDateContent = allDateProgress && allDateProgress.length > 0;
    const hasUnitContent = allUnitProgress && allUnitProgress.length > 0;
    const hasTooltipContent = hasUnitContent || hasDateContent || dateRemaining;

    if (hasTooltipContent) {
      return (
        <WrapTooltip content={<ProgressTooltipContent allUnitProgress={allUnitProgress} allDateProgress={allDateProgress} dateRemaining={dateRemaining} />}>
          {progressBar}
        </WrapTooltip>
      );
    }
  }

  return progressBar;
}
